import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  X, Type, MapPin, Wand2, Trash2, Check, Users, ChevronDown, Smile, BarChart3,
  AtSign, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight, Underline as UnderlineIcon,
  Pencil, Eraser, Undo2, Redo2, Sparkles, Copy as CopyIcon, Sticker, Send, Search,
} from 'lucide-react';
import api, { uploadMedia } from '../lib/api';

/**
 * StoryEditor v2 — Instagram-style.
 *
 * Surfaces:
 *  - Toolbar: Close · Text(Aa) · Draw · Sticker · Filters(wand) · Location
 *  - Tap empty canvas → instant text-create at tap point
 *  - Active-text inline toolbar (alignment, underline, font, style pill, color)
 *  - Drawing layer (pen/marker/highlighter/neon/eraser) with undo/redo
 *  - Stickers: location/mention/poll/emoji/link (rendered with same gesture system)
 *  - Snap guides: H/V center + horizontal safe zones (header/footer)
 *  - Drafts: auto-save to localStorage keyed by mediaUrl; offer restore on next open
 *  - Two-finger pinch+rotate + drag for any selected overlay
 */

const FONT_STYLES = [
  { key: 'classic',     label: 'Classic',     className: 'font-bold tracking-tight' },
  { key: 'modern',      label: 'Modern',      className: 'font-black tracking-tighter' },
  { key: 'neon',        label: 'Neon',        className: 'font-extrabold italic tracking-wide' },
  { key: 'typewriter',  label: 'Typewriter',  className: 'font-mono font-bold' },
  { key: 'strong',      label: 'Strong',      className: 'font-black uppercase tracking-widest' },
];

// 0 = no pill; 1 = solid pill behind text; 2 = translucent pill
const PILL_STYLES = ['none', 'solid', 'translucent', 'outline'];

const PALETTE_PRIMARY = ['#FFFFFF', '#000000', '#B6E600', '#FF3B30', '#FFD60A', '#FF9500', '#007AFF', '#5856D6', '#FF2D95', '#00C7BE'];
const PALETTE_NEUTRAL = ['#F5F5F0', '#D5D5CE', '#9B9EA3', '#6B6E73', '#3F3F3F'];
const PALETTE_CHROMA  = ['#FFB6C1', '#FF6B9D', '#A78BFA', '#60A5FA', '#34D399', '#A3E635', '#FACC15', '#FB923C'];

const FILTERS = [
  { key: '',         label: 'Original', css: '' },
  { key: 'warm',     label: 'Warm',     css: 'saturate(115%) contrast(105%) brightness(102%) sepia(8%)' },
  { key: 'cool',     label: 'Cool',     css: 'saturate(105%) contrast(108%) brightness(100%) hue-rotate(-8deg)' },
  { key: 'bw',       label: 'B&W',      css: 'grayscale(100%) contrast(108%)' },
  { key: 'vivid',    label: 'Vivid',    css: 'saturate(140%) contrast(110%)' },
  { key: 'fade',     label: 'Fade',     css: 'saturate(70%) contrast(92%) brightness(105%)' },
  { key: 'mono',     label: 'Mono',     css: 'grayscale(100%) brightness(105%) contrast(95%)' },
  { key: 'glow',     label: 'Glow',     css: 'saturate(110%) brightness(108%) blur(0.4px)' },
];
const filterCss = (key) => (FILTERS.find(f => f.key === key) || FILTERS[0]).css;

const AUDIENCE_OPTIONS = [
  { key: 'everyone',      label: 'Your Story',    subtitle: 'Visible to everyone who follows you', icon: '🌐' },
  { key: 'close_friends', label: 'Close Friends', subtitle: 'Only your close friends see this',    icon: '💚' },
];

const STORY_DRAFT_KEY = 'asap_story_draft';
const SNAP_TOLERANCE_PCT = 1.8;

const BRUSH_TYPES = [
  { key: 'pen',         label: 'Pen',         lineCap: 'round', alpha: 1.0,  composite: 'source-over' },
  { key: 'marker',      label: 'Marker',      lineCap: 'square', alpha: 0.85, composite: 'source-over' },
  { key: 'highlighter', label: 'Highlight',   lineCap: 'square', alpha: 0.35, composite: 'multiply'   },
  { key: 'neon',        label: 'Neon',        lineCap: 'round', alpha: 1.0,  composite: 'source-over', glow: true },
  { key: 'eraser',      label: 'Eraser',      lineCap: 'round', alpha: 1.0,  composite: 'destination-out' },
];

/* ─── Image recompression — keep this from V32, scaled gentler so quality survives ─── */
async function recompressImage(file) {
  if (!file || !file.type?.startsWith('image/')) return file;
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const outW = 1080, outH = 1920;
    const canvas = document.createElement('canvas');
    canvas.width = outW; canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, outW, outH);
    const srcA = img.width / img.height;
    const dstA = outW / outH;
    let dw = outW, dh = outH, dx = 0, dy = 0;
    if (srcA > dstA) { dh = outW / srcA; dy = (outH - dh) / 2; }
    else { dw = outH * srcA; dx = (outW - dw) / 2; }
    ctx.drawImage(img, dx, dy, dw, dh);
    URL.revokeObjectURL(url);
    const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.94));
    if (!blob) return file;
    return new File([blob], `story_${Date.now()}.jpg`, { type: 'image/jpeg' });
  } catch { return file; }
}

export default function StoryEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mediaUrl, mediaType, mediaFile, local } = location.state || {};

  // Overlays = text + stickers (drawing has its own layer)
  const [overlays, setOverlays] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editingText, setEditingText] = useState(null);

  // Drawing layer
  const [drawMode, setDrawMode] = useState(false);
  const [brush, setBrush] = useState('pen');
  const [brushColor, setBrushColor] = useState('#B6E600');
  const [brushSize, setBrushSize] = useState(8);
  const drawingCanvasRef = useRef(null);
  const undoStack = useRef([]); // stack of ImageData snapshots
  const redoStack = useRef([]);

  // Sticker picker
  const [showStickerPicker, setShowStickerPicker] = useState(false);

  // Snap guides visibility
  const [showSnapH, setShowSnapH] = useState(false);
  const [showSnapV, setShowSnapV] = useState(false);

  // Recent colors
  const [recentColors, setRecentColors] = useState(() => {
    try { return JSON.parse(localStorage.getItem('asap_recent_colors') || '[]').slice(0, 8); }
    catch { return []; }
  });

  // Existing flows
  const [caption, setCaption] = useState('');
  const [locationText, setLocationText] = useState('');
  const [filter, setFilter] = useState('');
  const [audience, setAudience] = useState('everyone');
  const [showFilterStrip, setShowFilterStrip] = useState(false);
  const [showAudience, setShowAudience] = useState(false);
  const [showLocationInput, setShowLocationInput] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');

  // Color picker visibility
  const [showColorGrid, setShowColorGrid] = useState(false);

  // Draft restore prompt
  const [draftPrompt, setDraftPrompt] = useState(null);

  const canvasRef = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  /* ── Keyboard-safe ── */
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handler = () => {
      const kb = Math.max(0, window.innerHeight - vv.height);
      document.documentElement.style.setProperty('--editor-kb', `${kb}px`);
    };
    vv.addEventListener('resize', handler);
    vv.addEventListener('scroll', handler);
    handler();
    return () => {
      vv.removeEventListener('resize', handler);
      vv.removeEventListener('scroll', handler);
      document.documentElement.style.setProperty('--editor-kb', '0px');
    };
  }, []);

  useEffect(() => {
    if (!mediaUrl) { navigate(-1); return; }
    document.body.classList.add('chat-locked');
    return () => document.body.classList.remove('chat-locked');
  }, [mediaUrl, navigate]);

  /* ── Draft auto-save ── */
  useEffect(() => {
    if (!mediaUrl) return;
    try {
      const raw = localStorage.getItem(STORY_DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.mediaUrl === mediaUrl && (d.overlays?.length || d.caption || d.locationText || d.filter)) {
          setDraftPrompt(d);
        }
      }
    } catch {/* ignore */}
  }, [mediaUrl]);

  useEffect(() => {
    if (!mediaUrl) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORY_DRAFT_KEY, JSON.stringify({
          mediaUrl, mediaType, caption, locationText, filter, audience, overlays,
          savedAt: Date.now(),
        }));
      } catch {/* ignore */}
    }, 600);
    return () => clearTimeout(t);
  }, [mediaUrl, mediaType, caption, locationText, filter, audience, overlays]);

  const restoreDraft = () => {
    if (!draftPrompt) return;
    setOverlays(draftPrompt.overlays || []);
    setCaption(draftPrompt.caption || '');
    setLocationText(draftPrompt.locationText || '');
    setFilter(draftPrompt.filter || '');
    setAudience(draftPrompt.audience || 'everyone');
    setDraftPrompt(null);
  };
  const discardDraft = () => {
    try { localStorage.removeItem(STORY_DRAFT_KEY); } catch {/* ignore */}
    setDraftPrompt(null);
  };

  /* ── Recent colors helper ── */
  const pushRecentColor = (c) => {
    setRecentColors(prev => {
      const next = [c, ...prev.filter(x => x !== c)].slice(0, 8);
      try { localStorage.setItem('asap_recent_colors', JSON.stringify(next)); } catch {/* ignore */}
      return next;
    });
  };

  /* ── Overlay helpers ── */
  // Counter-based ID so the create function is pure (no Date.now()/Math.random()
  // in render path) — pleases react-hooks/purity lint.
  const overlayIdSeq = useRef(0);
  const nextOverlayId = () => {
    overlayIdSeq.current += 1;
    return `ov-${overlayIdSeq.current}`;
  };

  const addTextOverlay = (xPct = 50, yPct = 50) => {
    const id = nextOverlayId();
    const ov = {
      id, type: 'text', text: '',
      color: '#FFFFFF', fontKey: 'classic', align: 'center',
      pill: 0, underline: false, opacity: 1,
      x: xPct, y: yPct, scale: 1, rotation: 0,
    };
    setOverlays(prev => [...prev, ov]);
    setSelectedId(id);
    setEditingText({ id, value: '', color: ov.color, fontKey: ov.fontKey });
  };

  const addStickerOverlay = (sticker) => {
    const id = nextOverlayId();
    const ov = { id, type: 'sticker', sticker, x: 50, y: 50, scale: 1, rotation: 0 };
    setOverlays(prev => [...prev, ov]);
    setSelectedId(id);
    setShowStickerPicker(false);
  };

  const updateOverlay = (id, patch) => {
    setOverlays(prev => prev.map(o => o.id === id ? { ...o, ...patch } : o));
  };

  const deleteOverlay = (id) => {
    setOverlays(prev => prev.filter(o => o.id !== id));
    setSelectedId(null);
    setEditingText(null);
  };

  const duplicateOverlay = (id) => {
    const newId = nextOverlayId();
    setOverlays(prev => {
      const src = prev.find(o => o.id === id);
      if (!src) return prev;
      const cp = { ...src, id: newId, x: Math.min(95, src.x + 6), y: Math.min(95, src.y + 6) };
      return [...prev, cp];
    });
  };

  const bringForward = (id) => {
    setOverlays(prev => {
      const idx = prev.findIndex(o => o.id === id);
      if (idx < 0 || idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };
  const sendBackward = (id) => {
    setOverlays(prev => {
      const idx = prev.findIndex(o => o.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx], next[idx - 1]] = [next[idx - 1], next[idx]];
      return next;
    });
  };

  /* ── Gestures (drag + pinch-resize + rotate) ── */
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const angle = (a, b) => Math.atan2(b.y - a.y, b.x - a.x);
  const pctFromEvent = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 50, y: 50 };
    return { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 };
  };

  const onPointerDownOverlay = (e, id) => {
    if (editingText || drawMode) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setSelectedId(id);
    const ov = overlays.find(o => o.id === id);
    if (!ov) return;
    if (pointers.current.size === 1) {
      gesture.current = { mode: 'drag', startPct: { ...pctFromEvent(e) }, ovStart: { x: ov.x, y: ov.y } };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = { mode: 'pinch', baseDist: dist(a, b), baseScale: ov.scale, baseAngle: angle(a, b), baseRot: ov.rotation };
    }
  };

  const onPointerMoveOverlay = (e, id) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    const ov = overlays.find(o => o.id === id);
    if (!ov) return;
    if (g.mode === 'drag' && pointers.current.size === 1) {
      const cur = pctFromEvent(e);
      let nx = g.ovStart.x + (cur.x - g.startPct.x);
      let ny = g.ovStart.y + (cur.y - g.startPct.y);
      // Snap to center axes
      const sH = Math.abs(nx - 50) < SNAP_TOLERANCE_PCT;
      const sV = Math.abs(ny - 50) < SNAP_TOLERANCE_PCT;
      if (sH) nx = 50;
      if (sV) ny = 50;
      setShowSnapH(sH);
      setShowSnapV(sV);
      nx = Math.max(2, Math.min(98, nx));
      ny = Math.max(2, Math.min(98, ny));
      updateOverlay(id, { x: nx, y: ny });
    } else if (g.mode === 'pinch' && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const d = dist(a, b);
      const newScale = Math.max(0.4, Math.min(4, g.baseScale * (d / Math.max(g.baseDist, 1))));
      const deltaA = angle(a, b) - g.baseAngle;
      const newRot = g.baseRot + (deltaA * 180) / Math.PI;
      updateOverlay(id, { scale: newScale, rotation: newRot });
    }
  };

  const onPointerUpOverlay = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) gesture.current = null;
    setShowSnapH(false);
    setShowSnapV(false);
  };

  /* ── Tap-anywhere on canvas → add text ── */
  // Use a ref + RAF flag for throttling instead of Date.now() (purity lint).
  const canvasTapBlocked = useRef(false);
  const handleCanvasTap = (e) => {
    // Ignore taps that originated on an overlay (overlays stopPropagation)
    if (e.target !== canvasRef.current && !e.target.classList?.contains?.('canvas-bg')) return;
    if (drawMode || editingText) return;
    if (selectedId) {
      // Tap on empty canvas with something selected → deselect first; require a
      // second tap to start adding new text. Matches Instagram exactly.
      setSelectedId(null);
      return;
    }
    if (canvasTapBlocked.current) return;
    canvasTapBlocked.current = true;
    setTimeout(() => { canvasTapBlocked.current = false; }, 80);
    const pt = pctFromEvent(e);
    addTextOverlay(pt.x, pt.y);
  };
  const onCanvasPointerDown = handleCanvasTap;

  /* ── Drawing canvas — setup + handlers ── */
  const setupDrawCanvas = useCallback(() => {
    const c = drawingCanvasRef.current;
    const host = canvasRef.current;
    if (!c || !host) return;
    const rect = host.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (c.width !== Math.round(rect.width * dpr) || c.height !== Math.round(rect.height * dpr)) {
      c.width = Math.round(rect.width * dpr);
      c.height = Math.round(rect.height * dpr);
      const ctx = c.getContext('2d');
      ctx.scale(dpr, dpr);
    }
  }, []);

  useLayoutEffect(() => {
    if (drawMode) setupDrawCanvas();
  }, [drawMode, setupDrawCanvas]);

  const drawing = useRef(null);
  const pushUndo = () => {
    const c = drawingCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    try {
      undoStack.current.push(ctx.getImageData(0, 0, c.width, c.height));
      if (undoStack.current.length > 30) undoStack.current.shift();
      redoStack.current.length = 0;
    } catch {/* security errors ignored */}
  };

  const onDrawDown = (e) => {
    if (!drawMode) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const c = drawingCanvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext('2d');
    const b = BRUSH_TYPES.find(x => x.key === brush) || BRUSH_TYPES[0];
    pushUndo();
    ctx.globalCompositeOperation = b.composite;
    ctx.globalAlpha = b.alpha;
    ctx.strokeStyle = brushColor;
    ctx.fillStyle = brushColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = b.lineCap;
    ctx.lineJoin = 'round';
    if (b.glow) {
      ctx.shadowColor = brushColor;
      ctx.shadowBlur = brushSize * 1.6;
    } else {
      ctx.shadowBlur = 0;
    }
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    drawing.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onDrawMove = (e) => {
    if (!drawMode || !drawing.current) return;
    e.preventDefault();
    const c = drawingCanvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };
  const onDrawUp = () => {
    drawing.current = null;
  };

  const undoDraw = () => {
    const c = drawingCanvasRef.current;
    if (!c) return;
    const last = undoStack.current.pop();
    const ctx = c.getContext('2d');
    if (last) {
      try {
        redoStack.current.push(ctx.getImageData(0, 0, c.width, c.height));
      } catch {/* ignore */}
      ctx.putImageData(last, 0, 0);
    } else {
      ctx.clearRect(0, 0, c.width, c.height);
    }
  };
  const redoDraw = () => {
    const c = drawingCanvasRef.current;
    if (!c) return;
    const last = redoStack.current.pop();
    if (last) {
      const ctx = c.getContext('2d');
      try { undoStack.current.push(ctx.getImageData(0, 0, c.width, c.height)); } catch {/* ignore */}
      ctx.putImageData(last, 0, 0);
    }
  };

  /* ── Editing text bubble ── */
  const startEditingText = (ov) => {
    if (drawMode) return;
    setSelectedId(ov.id);
    setEditingText({ id: ov.id, value: ov.text, color: ov.color, fontKey: ov.fontKey });
  };
  const commitEditingText = () => {
    if (!editingText) return;
    const trimmed = (editingText.value || '').replace(/[\r\n]+$/g, '');
    if (!trimmed) {
      deleteOverlay(editingText.id);
    } else {
      updateOverlay(editingText.id, { text: trimmed, color: editingText.color, fontKey: editingText.fontKey });
      pushRecentColor(editingText.color);
    }
    setEditingText(null);
  };

  /* ── Posting ── */
  const handleShare = async () => {
    if (posting) return;
    setPosting(true);
    setPostError('');
    const textOverlays = overlays.filter(o => o.type === 'text');
    const inlineMentions = new Set();
    textOverlays.forEach(o => {
      (o.text.match(/@([a-zA-Z0-9_]{1,30})/g) || []).forEach(m => inlineMentions.add(m.slice(1)));
    });
    const mentionStickers = overlays.filter(o => o.type === 'sticker' && o.sticker?.type === 'mention')
      .map(o => o.sticker?.username).filter(Boolean);
    mentionStickers.forEach(u => inlineMentions.add(u));
    const allMentions = Array.from(inlineMentions).slice(0, 30);
    try {
      let finalUrl = mediaUrl;
      let finalType = mediaType || 'image';
      if (local && mediaFile) {
        const fileForUpload = finalType === 'image' ? await recompressImage(mediaFile) : mediaFile;
        const up = await uploadMedia(fileForUpload);
        finalUrl = up.url;
        finalType = up.type || finalType;
      }
      const hasLocationSticker = overlays.some(o => o.type === 'sticker' && o.sticker?.type === 'location');
      // Sanity guard — historically the editor copied the sticker's name into
      // the `location` field too, causing a second chip to render at the
      // bottom of the story (different position from where the user placed
      // the sticker). Now: stickers carry their own position via `overlays`;
      // the plain `location` field is only used when the user types a
      // location via the legacy text input.
      void hasLocationSticker;
      const payload = {
        media_url: finalUrl,
        media_type: finalType,
        caption: (caption || '').slice(0, 200),
        audience,
        mentions: allMentions,
        location: locationText || '',
        filter,
        overlays: overlays.map(o => ({
          id: o.id,
          type: o.type,
          x: o.x, y: o.y, scale: o.scale, rotation: o.rotation,
          ...(o.type === 'text' ? {
            text: o.text, color: o.color, fontKey: o.fontKey,
            align: o.align, pill: o.pill, underline: o.underline, opacity: o.opacity,
          } : {}),
          ...(o.type === 'sticker' ? { sticker: o.sticker } : {}),
        })),
      };
      await api.post('/stories', payload);
      try { localStorage.removeItem(STORY_DRAFT_KEY); } catch {/* ignore */}
      if (local && mediaUrl?.startsWith('blob:')) {
        try { URL.revokeObjectURL(mediaUrl); } catch {/* ignore */}
      }
      const { data } = await api.get('/auth/me').catch(() => ({ data: {} }));
      const uid = data?.user?.id;
      if (uid) navigate(`/stories/${uid}`, { replace: true });
      else navigate('/profile', { replace: true });
    } catch (err) {
      setPostError(err.response?.data?.detail || 'Failed to post. Try again.');
      setPosting(false);
    }
  };

  /* ── Derived ── */
  const selectedOverlay = useMemo(
    () => overlays.find(o => o.id === selectedId) || null,
    [overlays, selectedId]
  );
  const isTextSelected = selectedOverlay?.type === 'text';

  if (!mediaUrl) return null;

  /* ── Rendering helpers ── */
  const renderTextOverlay = (ov) => {
    const fs = FONT_STYLES.find(f => f.key === ov.fontKey) || FONT_STYLES[0];
    const align = ov.align || 'center';
    const pillKind = PILL_STYLES[ov.pill || 0];
    let bg = 'transparent';
    let textStyle = {};
    if (pillKind === 'solid') {
      bg = ov.color;
      textStyle.color = pickInk(ov.color);
    } else if (pillKind === 'translucent') {
      bg = withAlpha(ov.color, 0.35);
      textStyle.color = '#FFFFFF';
    } else if (pillKind === 'outline') {
      bg = 'transparent';
      textStyle.color = ov.color;
      textStyle.WebkitTextStroke = `1.5px ${pickInk(ov.color)}`;
      textStyle.textShadow = `0 1px 2px rgba(0,0,0,0.55)`;
    } else {
      textStyle.color = ov.color;
      textStyle.textShadow = '0 1px 3px rgba(0,0,0,0.45)';
    }
    if (ov.underline) textStyle.textDecoration = 'underline';
    textStyle.opacity = ov.opacity ?? 1;
    // CRITICAL parity with viewer: display:block + width:max-content so the box
    // grows to fit the widest line, then text-align positions shorter lines
    // inside. inline-block here collapses to the first line on certain mobile
    // browsers and breaks alignment for multi-line text.
    return (
      <div
        className={`whitespace-pre-wrap break-words ${fs.className}`}
        style={{
          display: 'block',
          width: 'max-content',
          maxWidth: '78vw',
          fontSize: 28,
          lineHeight: 1.18,
          padding: bg !== 'transparent' ? '6px 12px' : '2px 0',
          borderRadius: bg !== 'transparent' ? 8 : 0,
          textAlign: align,
          background: bg,
          ...textStyle,
        }}
        data-testid={`overlay-text-${ov.id}`}
      >
        {ov.text || ' '}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black z-[10050] overflow-hidden select-none" style={{ height: '100dvh', width: '100vw', touchAction: 'none' }} data-testid="story-editor">
      {/* 9:16 canvas */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          ref={canvasRef}
          className="relative bg-black overflow-hidden canvas-bg"
          style={{ aspectRatio: '9 / 16', height: '100dvh', maxHeight: '100dvh', width: 'auto', maxWidth: '100vw' }}
          onPointerDown={onCanvasPointerDown}
          onClick={handleCanvasTap}
          data-testid="story-canvas"
        >
          {/* Blurred backdrop */}
          {mediaType === 'video' ? (
            <video src={mediaUrl} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-70 pointer-events-none" />
          ) : (
            <img loading="lazy" decoding="async" src={mediaUrl} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-70 pointer-events-none" />
          )}
          {/* Foreground media */}
          {mediaType === 'video' ? (
            <video src={mediaUrl} autoPlay loop muted playsInline className="relative w-full h-full object-contain pointer-events-none" style={{ filter: filterCss(filter) }} />
          ) : (
            <img loading="lazy" decoding="async" src={mediaUrl} alt="" className="relative w-full h-full object-contain pointer-events-none" style={{ filter: filterCss(filter) }} />
          )}

          {/* Drawing layer */}
          <canvas
            ref={drawingCanvasRef}
            className={`absolute inset-0 w-full h-full ${drawMode ? '' : 'pointer-events-none'}`}
            data-testid="draw-canvas"
            onPointerDown={onDrawDown}
            onPointerMove={onDrawMove}
            onPointerUp={onDrawUp}
            onPointerCancel={onDrawUp}
            style={{ touchAction: 'none' }}
          />

          {/* Snap guides */}
          {showSnapH && <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[#B6E600]/80 pointer-events-none" data-testid="snap-h" />}
          {showSnapV && <div className="absolute left-0 right-0 top-1/2 h-px bg-[#B6E600]/80 pointer-events-none" data-testid="snap-v" />}

          {/* Overlays */}
          {overlays.map(ov => {
            const isSel = ov.id === selectedId;
            const isEditing = editingText?.id === ov.id;
            return (
              <div
                key={ov.id}
                onPointerDown={(e) => onPointerDownOverlay(e, ov.id)}
                onPointerMove={(e) => onPointerMoveOverlay(e, ov.id)}
                onPointerUp={onPointerUpOverlay}
                onPointerCancel={onPointerUpOverlay}
                onClick={(e) => { e.stopPropagation(); if (ov.type === 'text' && !editingText) startEditingText(ov); }}
                className="absolute"
                style={{
                  left: `${ov.x}%`,
                  top: `${ov.y}%`,
                  transform: `translate(-50%, -50%) rotate(${ov.rotation}deg) scale(${ov.scale})`,
                  touchAction: 'none',
                  zIndex: isSel ? 30 : 20,
                }}
                data-testid={`overlay-${ov.id}`}
              >
                {ov.type === 'text' && !isEditing && renderTextOverlay(ov)}
                {ov.type === 'sticker' && <StickerView sticker={ov.sticker} isSel={isSel} />}
                {isSel && !isEditing && (
                  <div className="absolute -top-2 -right-2 flex flex-col gap-1">
                    <button onPointerDown={e => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); duplicateOverlay(ov.id); }} className="w-7 h-7 rounded-full bg-white text-[#111111] flex items-center justify-center shadow" data-testid={`dup-${ov.id}`}>
                      <CopyIcon size={12} />
                    </button>
                    <button onPointerDown={e => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); deleteOverlay(ov.id); }} className="w-7 h-7 rounded-full bg-[#FF3B30] text-white flex items-center justify-center shadow" data-testid={`del-${ov.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Top toolbar */}
      {!editingText && (
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between gap-2 p-3 pt-[env(safe-area-inset-top,16px)] z-[60]">
          <button data-testid="story-close" onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-black/45 backdrop-blur text-white flex items-center justify-center active:scale-90"><X size={20} /></button>
          <div className="flex items-center gap-2">
            <ToolButton testId="tool-text" active={false} onClick={() => addTextOverlay(50, 35)}><Type size={20} /></ToolButton>
            <ToolButton testId="tool-draw" active={drawMode} onClick={() => { setDrawMode(v => !v); setSelectedId(null); }}><Pencil size={20} /></ToolButton>
            <ToolButton testId="tool-sticker" active={showStickerPicker} onClick={() => setShowStickerPicker(true)}><Sticker size={20} /></ToolButton>
            <ToolButton testId="tool-filters" active={showFilterStrip} onClick={() => setShowFilterStrip(v => !v)}><Wand2 size={20} /></ToolButton>
            <ToolButton testId="tool-location" active={showLocationInput} onClick={() => setShowLocationInput(true)}><MapPin size={20} /></ToolButton>
          </div>
        </div>
      )}

      {/* Drawing toolbar */}
      {drawMode && !editingText && (
        <div className="absolute top-[calc(env(safe-area-inset-top,16px)+56px)] left-0 right-0 px-3 z-[60]" data-testid="draw-toolbar">
          <div className="bg-black/55 backdrop-blur rounded-2xl p-2 flex items-center gap-1.5">
            {BRUSH_TYPES.map(b => (
              <button
                key={b.key}
                onClick={() => setBrush(b.key)}
                data-testid={`brush-${b.key}`}
                className={`flex-1 py-2 rounded-xl text-[10px] font-bold transition-colors ${brush === b.key ? 'bg-white text-[#111111]' : 'text-white/80'}`}
              >
                {b.label}
              </button>
            ))}
            <button onClick={undoDraw} data-testid="brush-undo" className="w-9 h-9 flex items-center justify-center text-white"><Undo2 size={16} /></button>
            <button onClick={redoDraw} data-testid="brush-redo" className="w-9 h-9 flex items-center justify-center text-white"><Redo2 size={16} /></button>
          </div>
          <div className="mt-2 px-1 flex items-center gap-3">
            <input
              type="range" min="2" max="40" step="1" value={brushSize}
              onChange={e => setBrushSize(parseInt(e.target.value, 10))}
              data-testid="brush-size"
              className="flex-1 accent-[#B6E600]"
            />
            <button onClick={() => setShowColorGrid(true)} data-testid="brush-color" className="w-9 h-9 rounded-full border-2 border-white shadow-md" style={{ background: brushColor }} />
          </div>
        </div>
      )}

      {/* Active text inline toolbar */}
      {!drawMode && isTextSelected && !editingText && (
        <div className="absolute bottom-[calc(env(safe-area-inset-bottom,12px)+72px)] left-0 right-0 px-3 z-[60]" data-testid="text-toolbar">
          <div className="bg-black/55 backdrop-blur rounded-2xl p-2 flex items-center gap-1.5">
            <ChipBtn active={selectedOverlay.align === 'left'}   onClick={() => updateOverlay(selectedOverlay.id, { align: 'left' })}   testId="align-left"><AlignLeft size={16} /></ChipBtn>
            <ChipBtn active={selectedOverlay.align === 'center'} onClick={() => updateOverlay(selectedOverlay.id, { align: 'center' })} testId="align-center"><AlignCenter size={16} /></ChipBtn>
            <ChipBtn active={selectedOverlay.align === 'right'}  onClick={() => updateOverlay(selectedOverlay.id, { align: 'right' })}  testId="align-right"><AlignRight size={16} /></ChipBtn>
            <ChipBtn active={!!selectedOverlay.underline} onClick={() => updateOverlay(selectedOverlay.id, { underline: !selectedOverlay.underline })} testId="text-underline"><UnderlineIcon size={16} /></ChipBtn>
            <ChipBtn active={(selectedOverlay.pill || 0) > 0} onClick={() => updateOverlay(selectedOverlay.id, { pill: ((selectedOverlay.pill || 0) + 1) % PILL_STYLES.length })} testId="text-pill">
              <span className="text-[11px] font-bold">Aa</span>
            </ChipBtn>
            <ChipBtn onClick={() => updateOverlay(selectedOverlay.id, { fontKey: nextFont(selectedOverlay.fontKey) })} testId="text-font">
              <span className="text-[11px] font-bold">{(FONT_STYLES.find(f => f.key === selectedOverlay.fontKey)?.label || 'Font').slice(0, 4)}</span>
            </ChipBtn>
            <button onClick={() => setShowColorGrid(true)} data-testid="text-color" className="w-9 h-9 rounded-full border-2 border-white shadow-md flex-shrink-0" style={{ background: selectedOverlay.color }} />
          </div>
          <div className="mt-2 px-1 flex items-center gap-2 text-white">
            <Sparkles size={12} className="opacity-70" />
            <input type="range" min="0.3" max="1" step="0.05" value={selectedOverlay.opacity ?? 1}
              onChange={e => updateOverlay(selectedOverlay.id, { opacity: parseFloat(e.target.value) })}
              data-testid="text-opacity"
              className="flex-1 accent-[#B6E600]"
            />
          </div>
        </div>
      )}

      {/* Text editing mode */}
      {editingText && (
        <div className="absolute inset-0 bg-black/60 z-[70] flex flex-col" data-testid="text-edit-overlay">
          <div className="flex items-center justify-between p-3 pt-[env(safe-area-inset-top,16px)]">
            <button data-testid="text-edit-cancel" onClick={() => { deleteOverlay(editingText.id); setEditingText(null); }} className="text-white font-semibold text-sm">Cancel</button>
            <button data-testid="text-edit-done" onClick={commitEditingText} className="text-[#B6E600] font-bold text-sm">Done</button>
          </div>
          <div className="flex-1 flex items-center justify-center px-6" style={{ paddingBottom: 'var(--editor-kb, 0px)' }}>
            <textarea
              autoFocus
              value={editingText.value}
              onChange={e => setEditingText(prev => ({ ...prev, value: e.target.value }))}
              onBlur={commitEditingText}
              data-testid="text-edit-input"
              className={`w-full max-w-[78vw] text-3xl text-center bg-transparent text-white focus:outline-none resize-none ${(FONT_STYLES.find(f => f.key === editingText.fontKey) || FONT_STYLES[0]).className}`}
              style={{ color: editingText.color, textShadow: '0 1px 3px rgba(0,0,0,0.45)' }}
              placeholder="Type something…"
              rows={3}
            />
          </div>
          <div className="px-3 pb-3 pb-[env(safe-area-inset-bottom,12px)]">
            <ColorRow
              selected={editingText.color}
              recent={recentColors}
              onSelect={(c) => setEditingText(prev => ({ ...prev, color: c }))}
              testIdPrefix="edit-color"
            />
            <div className="flex items-center gap-1.5 mt-2 overflow-x-auto hide-scrollbar">
              {FONT_STYLES.map(f => (
                <button
                  key={f.key}
                  onClick={() => setEditingText(prev => ({ ...prev, fontKey: f.key }))}
                  data-testid={`edit-font-${f.key}`}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold ${editingText.fontKey === f.key ? 'bg-white text-[#111]' : 'bg-white/15 text-white'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter strip */}
      {showFilterStrip && !drawMode && !editingText && (
        <div className="absolute bottom-[calc(env(safe-area-inset-bottom,12px)+72px)] left-0 right-0 z-[60] px-3" data-testid="filter-strip">
          <div className="bg-black/45 backdrop-blur rounded-2xl p-2 flex items-center gap-2 overflow-x-auto hide-scrollbar">
            {FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key || 'original'}`}
                className={`flex-shrink-0 flex flex-col items-center gap-1.5 w-14 active:scale-95`}
              >
                <div className={`w-12 h-12 rounded-xl overflow-hidden border-2 ${filter === f.key ? 'border-white' : 'border-transparent'}`}>
                  <img loading="lazy" decoding="async" src={mediaUrl} alt="" className="w-full h-full object-cover" style={{ filter: f.css }} />
                </div>
                <span className="text-[10px] font-bold text-white truncate">{f.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Color grid */}
      {showColorGrid && (
        <div className="absolute inset-0 z-[80] bg-black/55 backdrop-blur" onClick={() => setShowColorGrid(false)} data-testid="color-grid-overlay">
          <div className="absolute bottom-0 left-0 right-0 bg-[#111111] rounded-t-3xl p-4 pb-[env(safe-area-inset-bottom,16px)]" onClick={e => e.stopPropagation()} data-testid="color-grid">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white font-bold text-sm">Color</p>
              <button onClick={() => setShowColorGrid(false)} className="text-white/70 text-xs font-semibold">Done</button>
            </div>
            {recentColors.length > 0 && (
              <div className="mb-3">
                <p className="text-white/50 text-[10px] uppercase tracking-wider mb-1.5">Recent</p>
                <div className="flex gap-1.5">
                  {recentColors.map(c => (
                    <button key={c} onClick={() => applyChosenColor(c)} data-testid={`recent-${c}`} className="w-8 h-8 rounded-full border-2 border-white/30" style={{ background: c }} />
                  ))}
                </div>
              </div>
            )}
            <Swatches label="Primary" colors={PALETTE_PRIMARY} onPick={applyChosenColor} prefix="primary" />
            <Swatches label="Neutrals" colors={PALETTE_NEUTRAL} onPick={applyChosenColor} prefix="neutral" />
            <Swatches label="Chromatic" colors={PALETTE_CHROMA} onPick={applyChosenColor} prefix="chroma" />
          </div>
        </div>
      )}

      {/* Sticker picker */}
      {showStickerPicker && (
        <StickerPicker
          onClose={() => setShowStickerPicker(false)}
          onPick={addStickerOverlay}
        />
      )}

      {/* Bottom action bar — IG-style floating glass composer */}
      {!editingText && !drawMode && (
        <div className="absolute bottom-0 left-0 right-0 px-3 pb-[max(env(safe-area-inset-bottom),12px)] pt-2 z-[60]" data-testid="story-bottom-bar">
          <div className="bg-black/45 backdrop-blur-xl rounded-[28px] border border-white/15 shadow-[0_4px_24px_rgba(0,0,0,0.35)] px-3 py-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAudience(true)}
                data-testid="story-audience-btn"
                className="flex items-center gap-1 pl-1 pr-2.5 py-1.5 rounded-full bg-white/12 hover:bg-white/20 text-white text-[11px] font-bold flex-shrink-0 active:scale-95 transition"
              >
                <span className="text-base leading-none">{AUDIENCE_OPTIONS.find(a => a.key === audience)?.icon || '🌐'}</span>
                <span className="hidden xs:inline">{audience === 'close_friends' ? 'Friends' : 'Story'}</span>
                <ChevronDown size={12} />
              </button>
              <textarea
                value={caption}
                onChange={e => setCaption(e.target.value.slice(0, 200))}
                placeholder="Write a caption…"
                data-testid="story-caption"
                rows={1}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                }}
                className="flex-1 bg-transparent text-white text-sm placeholder-white/55 focus:outline-none resize-none leading-snug py-1.5 max-h-[96px]"
                style={{ overflowY: 'auto' }}
              />
              <button
                data-testid="story-share"
                disabled={posting}
                onClick={handleShare}
                className="flex-shrink-0 w-11 h-11 rounded-full bg-[#B6E600] text-[#111111] flex items-center justify-center disabled:opacity-50 active:scale-90 transition-transform shadow-lg"
                aria-label="Share story"
              >
                {posting ? (
                  <div className="w-4 h-4 border-2 border-[#111]/40 border-t-[#111] rounded-full animate-spin" />
                ) : (
                  <Send size={18} strokeWidth={2.5} />
                )}
              </button>
            </div>
            {caption.length > 0 && (
              <div className="flex justify-end pt-0.5 pr-1">
                <span className="text-[9px] font-bold text-white/45 tabular-nums">{caption.length}/200</span>
              </div>
            )}
          </div>
          {postError && <p className="text-center text-[#FF3B30] text-xs mt-2" data-testid="story-error">{postError}</p>}
        </div>
      )}

      {/* Audience selector */}
      {showAudience && (
        <div className="absolute inset-0 z-[80] flex items-end justify-center bg-black/60" onClick={() => setShowAudience(false)} data-testid="audience-overlay">
          <div className="w-full max-w-[480px] bg-white rounded-t-3xl p-3 pb-safe-area" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-[#E5E4DE] rounded-full" /></div>
            <p className="text-center font-bold text-sm py-2">Share to</p>
            {AUDIENCE_OPTIONS.map(a => (
              <button key={a.key} data-testid={`audience-${a.key}`} onClick={() => { setAudience(a.key); setShowAudience(false); }} className={`w-full p-3 rounded-2xl text-left active:bg-[#F1F0EA] ${audience === a.key ? 'bg-[#F1F0EA]' : ''}`}>
                <p className="text-sm font-bold">{a.icon} {a.label}</p>
                <p className="text-xs text-[#9B9EA3] mt-0.5">{a.subtitle}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Location input (alternative entry via toolbar pin — uses same autocomplete) */}
      {showLocationInput && (
        <div className="absolute inset-0 z-[80] flex items-end justify-center bg-black/60" onClick={() => setShowLocationInput(false)} data-testid="location-overlay">
          <div className="w-full max-w-[480px] bg-[#111111] rounded-t-3xl p-4 pb-[env(safe-area-inset-bottom,16px)] max-h-[70vh] overflow-y-auto hide-scrollbar" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center pt-1 pb-2"><div className="w-10 h-1 bg-white/20 rounded-full" /></div>
            <p className="text-center font-bold text-sm text-white pb-3">Tag a location</p>
            <LocationStickerForm onPick={(s) => {
              setLocationText(s.name || '');
              addStickerOverlay(s);
              setShowLocationInput(false);
            }} />
          </div>
        </div>
      )}

      {/* Draft restore prompt */}
      {draftPrompt && (
        <div className="absolute inset-0 z-[90] flex items-center justify-center bg-black/70" data-testid="draft-restore-overlay">
          <div className="bg-white rounded-3xl p-5 max-w-[320px] w-[88vw]">
            <h3 className="font-bold text-base text-[#111111]">Restore your draft?</h3>
            <p className="text-xs text-[#6B6E73] mt-1">You left a story in progress — pick up where you stopped, or start fresh.</p>
            <div className="mt-4 flex gap-2">
              <button onClick={discardDraft} data-testid="draft-discard" className="flex-1 py-2.5 rounded-full bg-[#F1F0EA] text-sm font-semibold">Start fresh</button>
              <button onClick={restoreDraft} data-testid="draft-restore" className="flex-1 py-2.5 rounded-full bg-[#B6E600] text-sm font-bold text-[#111111]">Restore</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function applyChosenColor(c) {
    if (editingText) setEditingText(prev => ({ ...prev, color: c }));
    else if (isTextSelected) updateOverlay(selectedOverlay.id, { color: c });
    else setBrushColor(c);
    pushRecentColor(c);
    setShowColorGrid(false);
  }
}

/* ─── Helpers ─── */
function nextFont(key) {
  const idx = FONT_STYLES.findIndex(f => f.key === key);
  return FONT_STYLES[(idx + 1) % FONT_STYLES.length].key;
}
function withAlpha(hex, a) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
function pickInk(hex) {
  // luminance-based ink pick: dark text on bright, white on dark
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return '#111111';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.6 ? '#111111' : '#FFFFFF';
}

/* ─── Small UI atoms ─── */
function ToolButton({ children, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`w-10 h-10 rounded-full backdrop-blur flex items-center justify-center active:scale-90 transition-transform ${active ? 'bg-[#B6E600] text-[#111111]' : 'bg-black/45 text-white'}`}>
      {children}
    </button>
  );
}

function ChipBtn({ children, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`w-9 h-9 rounded-xl flex items-center justify-center ${active ? 'bg-white text-[#111111]' : 'text-white'}`}>
      {children}
    </button>
  );
}

function ColorRow({ selected, recent, onSelect, testIdPrefix }) {
  const colors = Array.from(new Set([...recent, ...PALETTE_PRIMARY])).slice(0, 12);
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto hide-scrollbar">
      {colors.map(c => (
        <button key={c} onClick={() => onSelect(c)} data-testid={`${testIdPrefix}-${c}`} className={`w-8 h-8 rounded-full border-2 flex-shrink-0 ${selected === c ? 'border-[#B6E600] ring-2 ring-[#B6E600]/40' : 'border-white/30'}`} style={{ background: c }} />
      ))}
    </div>
  );
}

function Swatches({ label, colors, onPick, prefix }) {
  return (
    <div className="mb-3">
      <p className="text-white/50 text-[10px] uppercase tracking-wider mb-1.5">{label}</p>
      <div className="grid grid-cols-10 gap-1.5">
        {colors.map(c => (
          <button key={c} onClick={() => onPick(c)} data-testid={`swatch-${prefix}-${c}`} className="aspect-square rounded-full border-2 border-white/15" style={{ background: c }} />
        ))}
      </div>
    </div>
  );
}

function StickerView({ sticker, isSel }) {
  if (!sticker) return null;
  const ring = isSel ? 'ring-2 ring-white/60' : '';
  switch (sticker.type) {
    case 'location':
      return <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B6E600] text-[#111111] text-xs font-extrabold shadow-md ${ring}`}><MapPin size={14} /> {sticker.name || 'Location'}</span>;
    case 'mention':
      return <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-[#111111] text-xs font-extrabold shadow-md ${ring}`}><AtSign size={14} /> {sticker.username || 'user'}</span>;
    case 'poll':
      return (
        <div className={`px-3 py-2 rounded-2xl bg-white/90 text-[#111111] shadow-md min-w-[180px] ${ring}`}>
          <p className="text-[11px] font-bold text-center mb-1.5">{sticker.question || 'Yes or no?'}</p>
          <div className="flex gap-1.5">
            <span className="flex-1 text-center py-1 rounded-full bg-[#B6E600] text-[11px] font-bold">{sticker.optionA || 'Yes'}</span>
            <span className="flex-1 text-center py-1 rounded-full bg-[#FF6B9D] text-white text-[11px] font-bold">{sticker.optionB || 'No'}</span>
          </div>
        </div>
      );
    case 'link':
      return <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/75 text-white text-xs font-extrabold shadow-md ${ring}`}><LinkIcon size={14} /> {sticker.label || (sticker.url || '').replace(/^https?:\/\//i, '').slice(0, 24) || 'Link'}</span>;
    case 'emoji':
    default:
      return <span className="text-[64px] leading-none">{sticker.value || '✨'}</span>;
  }
}

function StickerPicker({ onClose, onPick }) {
  const [tab, setTab] = useState('emoji');
  const emojis = ['😍', '🥺', '😂', '🔥', '💯', '🎉', '✨', '💫', '🥂', '☕', '🌅', '🌙', '🌊', '⛅', '💚', '👀', '🤝', '🙌', '👏', '💃'];
  return (
    <div className="absolute inset-0 z-[80] bg-black/70 flex items-end" onClick={onClose} data-testid="sticker-picker">
      <div className="w-full bg-[#111111] rounded-t-3xl p-4 pb-[env(safe-area-inset-bottom,16px)] max-h-[78vh] overflow-y-auto hide-scrollbar" onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-1 pb-2"><div className="w-10 h-1 bg-white/20 rounded-full" /></div>
        <div className="flex gap-1.5 mb-3">
          {['emoji', 'location', 'mention', 'poll', 'link'].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`sticker-tab-${t}`} className={`flex-1 py-2 rounded-full text-xs font-bold capitalize ${tab === t ? 'bg-white text-[#111111]' : 'bg-white/10 text-white'}`}>{t}</button>
          ))}
        </div>
        {tab === 'emoji' && (
          <div className="grid grid-cols-6 gap-2">
            {emojis.map(e => (
              <button key={e} onClick={() => onPick({ type: 'emoji', value: e })} data-testid={`sticker-emoji-${e}`} className="aspect-square rounded-2xl bg-white/5 text-3xl flex items-center justify-center active:scale-90">{e}</button>
            ))}
          </div>
        )}
        {tab === 'location' && <LocationStickerForm onPick={onPick} />}
        {tab === 'mention' && <MentionStickerForm onPick={onPick} />}
        {tab === 'poll' && <PollStickerForm onPick={onPick} />}
        {tab === 'link' && <LinkStickerForm onPick={onPick} />}
      </div>
    </div>
  );
}

/* ─── Location autocomplete (Nominatim via /api/places/search) ─────────────
 * The provider is abstracted into a single fetch call so a Google Places swap
 * later only touches `searchPlaces()` — the UI shape stays identical.
 */
async function searchPlaces(q) {
  const trimmed = (q || '').trim();
  if (trimmed.length < 2) return [];
  try {
    const { default: api } = await import('../lib/api');
    const { data } = await api.get(`/places/search?q=${encodeURIComponent(trimmed)}`);
    return Array.isArray(data) ? data : [];
  } catch { return []; }
}

function LocationStickerForm({ onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncer = useRef(null);
  const reqSeq = useRef(0);

  const handleChange = (val) => {
    setQ(val);
    if (debouncer.current) clearTimeout(debouncer.current);
    if (val.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const mySeq = ++reqSeq.current;
    debouncer.current = setTimeout(() => {
      searchPlaces(val).then((r) => {
        if (mySeq !== reqSeq.current) return; // stale
        setResults(r);
        setLoading(false);
      });
    }, 300);
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
        <input
          autoFocus
          value={q}
          onChange={e => handleChange(e.target.value)}
          placeholder="Search a place…"
          data-testid="sticker-location-input"
          className="w-full bg-white/10 text-white rounded-2xl pl-11 pr-4 py-3 text-sm placeholder-white/40 focus:outline-none focus:bg-white/15"
        />
      </div>
      {loading && (
        <div className="flex justify-center py-4">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
      {!loading && q.trim().length >= 2 && results.length === 0 && (
        <div className="text-center py-4 space-y-2">
          <p className="text-white/50 text-xs">No matches found.</p>
          <button
            data-testid="sticker-location-add-custom"
            onClick={() => onPick({ type: 'location', name: q.trim() })}
            className="px-4 py-2 rounded-full bg-white/10 text-white text-xs font-bold active:scale-95"
          >
            Use “{q.trim()}” anyway
          </button>
        </div>
      )}
      {!loading && results.length > 0 && (
        <div className="max-h-[40vh] overflow-y-auto hide-scrollbar -mx-2 px-2 space-y-1">
          {results.map(r => (
            <button
              key={r.id}
              onClick={() => onPick({ type: 'location', name: r.name, address: r.address, lat: r.lat, lon: r.lon, emirate: r.emirate, region: r.region, area: r.area })}
              data-testid={`sticker-location-result-${r.id}`}
              className="w-full flex items-center gap-3 p-2.5 rounded-2xl bg-white/5 hover:bg-white/10 active:scale-[0.98] transition text-left"
            >
              <div className="w-9 h-9 rounded-full bg-[#B6E600]/20 flex items-center justify-center flex-shrink-0">
                <MapPin size={16} className="text-[#B6E600]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{r.name}</p>
                <p className="text-[11px] text-white/55 truncate">{r.address || r.display_name || ''}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Mention autocomplete via /api/explore/people ─────────────────────── */
async function searchPeople(q) {
  const trimmed = (q || '').trim().replace(/^@/, '');
  if (trimmed.length < 1) return [];
  try {
    const { default: api } = await import('../lib/api');
    const { data } = await api.get(`/explore/people?q=${encodeURIComponent(trimmed)}`);
    return Array.isArray(data) ? data.slice(0, 8) : [];
  } catch { return []; }
}

function MentionStickerForm({ onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncer = useRef(null);
  const reqSeq = useRef(0);

  const handleChange = (val) => {
    const cleaned = val.replace(/^@/, '');
    setQ(cleaned);
    if (debouncer.current) clearTimeout(debouncer.current);
    if (cleaned.trim().length < 1) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const mySeq = ++reqSeq.current;
    debouncer.current = setTimeout(() => {
      searchPeople(cleaned).then((r) => {
        if (mySeq !== reqSeq.current) return; // stale
        setResults(r);
        setLoading(false);
      });
    }, 250);
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <AtSign size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
        <input
          autoFocus
          value={q}
          onChange={e => handleChange(e.target.value)}
          placeholder="Search people…"
          data-testid="sticker-mention-input"
          className="w-full bg-white/10 text-white rounded-2xl pl-11 pr-4 py-3 text-sm placeholder-white/40 focus:outline-none focus:bg-white/15"
        />
      </div>
      {loading && (
        <div className="flex justify-center py-4">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
      {!loading && results.length > 0 && (
        <div className="max-h-[40vh] overflow-y-auto hide-scrollbar -mx-2 px-2 space-y-1">
          {results.map(u => (
            <button
              key={u.id}
              onClick={() => onPick({ type: 'mention', username: u.username, user_id: u.id, display_name: u.display_name })}
              data-testid={`sticker-mention-result-${u.username}`}
              className="w-full flex items-center gap-3 p-2.5 rounded-2xl bg-white/5 hover:bg-white/10 active:scale-[0.98] transition text-left"
            >
              {u.profile_photo
                ? <img loading="lazy" decoding="async" src={u.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                : <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white/70 flex-shrink-0">{(u.display_name || u.username || '?').slice(0, 2).toUpperCase()}</div>}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{u.display_name || u.username}</p>
                <p className="text-[11px] text-white/55 truncate">@{u.username}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {!loading && q.trim().length > 0 && results.length === 0 && (
        <div className="text-center py-4 space-y-2">
          <p className="text-white/50 text-xs">No matches found.</p>
          <button
            data-testid="sticker-mention-add-custom"
            onClick={() => onPick({ type: 'mention', username: q.trim() })}
            className="px-4 py-2 rounded-full bg-white/10 text-white text-xs font-bold active:scale-95"
          >
            Mention “@{q.trim()}” anyway
          </button>
        </div>
      )}
    </div>
  );
}

function PollStickerForm({ onPick }) {
  const [option, setOption] = useState({ q: '', a: '', b: '' });
  return (
    <div className="space-y-2">
      <input value={option.q} onChange={e => setOption({ ...option, q: e.target.value })} placeholder="Question" data-testid="sticker-poll-question" className="w-full bg-white/10 text-white rounded-2xl px-4 py-3 text-sm placeholder-white/40 focus:outline-none" />
      <div className="grid grid-cols-2 gap-2">
        <input value={option.a} onChange={e => setOption({ ...option, a: e.target.value })} placeholder="Yes" data-testid="sticker-poll-a" className="bg-white/10 text-white rounded-2xl px-4 py-3 text-sm placeholder-white/40 focus:outline-none" />
        <input value={option.b} onChange={e => setOption({ ...option, b: e.target.value })} placeholder="No" data-testid="sticker-poll-b" className="bg-white/10 text-white rounded-2xl px-4 py-3 text-sm placeholder-white/40 focus:outline-none" />
      </div>
      <button onClick={() => option.q.trim() && onPick({ type: 'poll', question: option.q.trim(), optionA: option.a.trim() || 'Yes', optionB: option.b.trim() || 'No' })} data-testid="sticker-poll-add" className="w-full py-3 rounded-full bg-[#B6E600] text-[#111] text-sm font-bold">Add</button>
    </div>
  );
}

function LinkStickerForm({ onPick }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const valid = /^[a-z][a-z0-9+\-.]*:\/\//i.test(url.trim()) || /^[\w-]+(\.[\w-]+)+(\/.*)?$/i.test(url.trim());
  return (
    <div className="space-y-2">
      <div className="relative">
        <LinkIcon size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
        <input
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https://your-link.com"
          data-testid="sticker-link-url"
          className="w-full bg-white/10 text-white rounded-2xl pl-11 pr-4 py-3 text-sm placeholder-white/40 focus:outline-none focus:bg-white/15"
        />
      </div>
      <input
        value={label}
        onChange={e => setLabel(e.target.value.slice(0, 32))}
        placeholder="Button label (optional)"
        data-testid="sticker-link-label"
        className="w-full bg-white/10 text-white rounded-2xl px-4 py-3 text-sm placeholder-white/40 focus:outline-none"
      />
      <button
        onClick={() => {
          const raw = url.trim();
          if (!raw) return;
          const href = /^[a-z][a-z0-9+\-.]*:\/\//i.test(raw) ? raw : `https://${raw}`;
          onPick({ type: 'link', url: href, label: label.trim() || raw.replace(/^https?:\/\//i, '').slice(0, 24) });
        }}
        disabled={!valid}
        data-testid="sticker-link-add"
        className="w-full py-3 rounded-full bg-[#B6E600] text-[#111] text-sm font-bold disabled:opacity-40"
      >
        Add Link
      </button>
      <p className="text-[10px] text-white/40 text-center">Tappable in story · Opens in a new tab</p>
    </div>
  );
}
